import { computed } from 'vue';
import {
  integer, maxLength, minLength, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage, linkedBoxRequiredValidator } from '@/helpers/CustomValidator';
import type { FlowOperatorMetadata, ValidationResult, Period, Dependencies } from '@/types/flow-types';
import Form from './Form.vue';

export interface FilledOutFormData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  grant_additional_delay: string;
  extra_time: Period;
  type: 'filled' | 'nofilled';
  linked_box: string;
  funnel_template_ids: string;
  initial_number_outputs?: number | null;
}

export const createDefaultData = (overrides: Partial<FilledOutFormData> = {}): FilledOutFormData => ({
  custom_title: '',
  custom_description: '',
  outputs_number: 1,
  grant_additional_delay: '1',
  extra_time: {
    value: 1,
    unit: 'DAY',
  },
  type: 'filled',
  linked_box: '[]',
  funnel_template_ids: '[]',
  ...overrides,
});

// Operators that can be linked to this filter (only those with forms, not email)
export const LINKABLE_OPERATOR_TYPES = [
  'boxdisplayfreemodel',
  'boxdisplayfreepopup',
];

const FilledOutForm: FlowOperatorMetadata<FilledOutFormData> = {
  id: 'filledoutform',
  kind: 'filter',
  label: 'automatedScenarios.filters.filledOutForm',
  icon: 'fa-rectangle-list',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],

  // This filter only appears if there's a linkable operator in the ancestors
  dependencies: {
    [TypeCampaignEnum.AUTOMATION]: {
      operators: {
        any_parents: LINKABLE_OPERATOR_TYPES,
      },
    },
    [TypeCampaignEnum.BULK]: {
      operators: {
        any_parents: LINKABLE_OPERATOR_TYPES,
      },
    },
  } as Dependencies,

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching', 'boxvisitpage'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxchoosebulklist', 'boxaudiencesetup'],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs(data: FilledOutFormData): number {
    return data.outputs_number ?? 1;
  },

  getDisplayLabel(data: FilledOutFormData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: FilledOutFormData): string | null {
    return data.custom_description || null;
  },

  async validate(data: FilledOutFormData): Promise<ValidationResult> {
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = computed(() => ({
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      type: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['filled', 'nofilled']),
      },
      linked_box: {
        linkedBoxRequiredValidator: linkedBoxRequiredValidator(),
      },
    }));

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: FilledOutFormData): FilledOutFormData {
    const cleanData = { ...data };
    if ('initial_number_outputs' in cleanData) {
      delete (cleanData as any).initial_number_outputs;
    }
    return cleanData;
  },
};

export default FilledOutForm;
